// The swimlane map: lanes down the side, steps laid out by the flow, handoffs routed between them.
// Click a step to open it; drag it to another lane; click its + handle to add the next step, drag it onto another
// step to connect them, or into a lane to add a connected step there; double-click (or start typing) to rename.
// The selected step gets a small toolbar. Double-click a lane's name to rename it. Click a handoff to open the step it
// leaves from. Drag the background to pan; Ctrl/Cmd + wheel zooms.
import { useEffect, useLayoutEffect, useRef, useState, type PointerEvent as RPointerEvent, type ReactNode } from "react";
import type { Diff, MapBody, Step } from "@couragemfg/api/processes-model";
import { SYSTEM_LABEL } from "@couragemfg/api/processes-model";
import { LANEH, route, sz, wrap, type Layout } from "./layout";

export type Active = { nodes: Set<string>; edges: Set<string>; list: string[] } | null;

export const sysText = (s: Step) => (s.type === "end" ? "" : s.screen ? `${SYSTEM_LABEL[s.system] ?? "Manual"} · ${s.screen}` : SYSTEM_LABEL[s.system] ?? "Manual");

type Props = {
  doc: MapBody; L: Layout; diff: Diff | null; active: Active; sel: string | null; walkCur: string | null; zoom: number; ro: boolean;
  comments: Record<string, number>; railWidth: number;
  rename: { id: string; value: string; all?: boolean } | null; // all: start with the name selected, so typing replaces it
  laneRename: { id: string; value: string } | null;
  /** The details panel is hidden: the toolbar offers to show it. */
  panelHidden: boolean;
  onSelect: (id: string | null) => void;
  onMoveLane: (id: string, laneId: string) => void;
  onConnect: (from: string, to: string) => void;
  /** Add the step after this one (a branch, from a decision), in a lane when given. */
  onAddNext: (from: string, laneId?: string) => void;
  onAddDecision: (from: string) => void;
  onDelete: (id: string) => void;
  onShowPanel: () => void;
  onStartRename: (id: string) => void;
  onRenameChange: (value: string) => void;
  /** byKey: Enter or Esc, which hands the keyboard back to the map; a blur doesn't. */
  onRenameEnd: (save: boolean, byKey?: boolean) => void;
  onLaneRename: (r: { id: string; value: string } | null, save?: boolean, byKey?: boolean) => void;
  onAddLane: () => void;
  onZoom: (z: number, at?: { clientX: number; clientY: number }) => void;
  scrollRef: React.RefObject<HTMLDivElement | null>;
};

type Drag =
  | { kind: "move"; id: string; x0: number; y0: number; moved: boolean; dx: number; dy: number; lane: number; off?: boolean }
  | { kind: "connect"; from: string; x0: number; y0: number; moved: boolean; x: number; y: number; target: string | null; self: boolean; inBoard: boolean }
  | { kind: "bg"; x0: number; y0: number; sx: number; sy: number; pan: boolean; moved: boolean; edge?: string };

export function MapCanvas(p: Props) {
  const { doc, L, zoom: z } = p;
  const svgRef = useRef<SVGSVGElement>(null);
  const [drag, setDrag] = useState<Drag | null>(null);
  const dragRef = useRef<Drag | null>(null);
  // A double-click (or double tap) on a step renames it. Any press anywhere else in between starts over.
  const lastTap = useRef<{ id: string | null; t: number; x: number; y: number }>({ id: null, t: 0, x: 0, y: 0 });
  useEffect(() => {
    const reset = (e: PointerEvent) => { if (!svgRef.current?.contains(e.target as Node)) lastTap.current = { id: null, t: 0, x: 0, y: 0 }; };
    window.addEventListener("pointerdown", reset, true);
    return () => window.removeEventListener("pointerdown", reset, true);
  }, []);
  // Enter or Esc ends a rename; the blur that follows (focus goes back to the map) must not end it a second time.
  const ended = useRef(false);
  const B = Object.fromEntries(doc.steps.map((s) => [s.id, s]));
  const set = (d: Drag | null) => { dragRef.current = d; setDrag(d); };
  const W = L.W, H = L.H;

  // Ctrl/Cmd + wheel zooms around the pointer; plain wheel scrolls.
  useEffect(() => {
    const el = p.scrollRef.current; if (!el) return;
    const onWheel = (e: WheelEvent) => { if (!(e.ctrlKey || e.metaKey)) return; e.preventDefault(); p.onZoom(z * (e.deltaY < 0 ? 1.1 : 1 / 1.1), e); };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  });

  const toBoard = (e: { clientX: number; clientY: number }) => {
    const r = svgRef.current!.getBoundingClientRect();
    return { x: (e.clientX - r.left) / z, y: (e.clientY - r.top) / z };
  };
  // Whether the pointer is over the visible part of the map (not the lane names, the details panel or the page around it).
  const onScreen = (e: { clientX: number; clientY: number }) => {
    const sc = p.scrollRef.current; if (!sc) return true;
    const v = sc.getBoundingClientRect();
    return e.clientX >= v.left + p.railWidth && e.clientX <= v.left + sc.clientWidth && e.clientY >= v.top && e.clientY <= v.top + sc.clientHeight;
  };
  const laneAt = (y: number) => Math.max(0, Math.min(doc.lanes.length - 1, Math.floor(y / LANEH)));
  const nodeAt = (pt: { x: number; y: number }) => {
    for (const id in L.pos) { const q = L.pos[id], s = B[id]; if (!s) continue; const { w, h } = sz(s); if (Math.abs(pt.x - q.x) <= w / 2 + 4 && Math.abs(pt.y - q.y) <= h / 2 + 4) return id; }
    return null;
  };
  const capture = (e: RPointerEvent<SVGSVGElement>) => { try { svgRef.current?.setPointerCapture(e.pointerId); } catch { /* not every browser */ } };

  const down = (e: RPointerEvent<SVGSVGElement>) => {
    if (e.button > 0) return;
    if (p.rename) p.onRenameEnd(true);
    if (p.laneRename) p.onLaneRename(p.laneRename, true);
    // The map takes the keyboard (Enter, Tab, typing to rename), without a focus ring for a click.
    p.scrollRef.current?.focus({ preventScroll: true, focusVisible: false } as FocusOptions);
    const t = e.target as Element;
    if (!t.closest("[data-node]")) lastTap.current = { id: null, t: 0, x: 0, y: 0 };
    const handle = t.closest("[data-handle]") as HTMLElement | null;
    if (handle && !p.ro) {
      const pt = toBoard(e);
      set({ kind: "connect", from: handle.dataset.handle!, x0: e.clientX, y0: e.clientY, moved: false, x: pt.x, y: pt.y, target: null, self: true, inBoard: true });
      capture(e); e.preventDefault(); return;
    }
    const node = t.closest("[data-node]") as HTMLElement | null;
    if (node) {
      const id = node.dataset.node!, now = Date.now(), lt = lastTap.current;
      const dbl = lt.id === id && now - lt.t < 400 && Math.hypot(e.clientX - lt.x, e.clientY - lt.y) < 10;
      lastTap.current = dbl ? { id: null, t: 0, x: 0, y: 0 } : { id, t: now, x: e.clientX, y: e.clientY };
      if (p.sel !== id) p.onSelect(id);
      // After this press is done with (its focus change included), so the name box keeps the focus.
      if (dbl && !p.ro) { e.preventDefault(); setTimeout(() => p.onStartRename(id), 0); return; }
      if (!p.ro) { set({ kind: "move", id, x0: e.clientX, y0: e.clientY, moved: false, dx: 0, dy: 0, lane: L.pos[id]?.li ?? 0 }); capture(e); }
      e.preventDefault(); return;
    }
    // A handoff: a click (not a drag or a swipe) opens the step it leaves from, where its next step or branches are changed.
    const edge = (t.closest("[data-edge]") as HTMLElement | null)?.dataset.edge?.split(">")[0];
    // The background: a mouse drags the map around; touch scrolls natively.
    const el = p.scrollRef.current;
    set({ kind: "bg", x0: e.clientX, y0: e.clientY, sx: el?.scrollLeft ?? 0, sy: el?.scrollTop ?? 0, pan: e.pointerType === "mouse", moved: false, edge });
    if (e.pointerType === "mouse") capture(e);
  };
  const move = (e: RPointerEvent<SVGSVGElement>) => {
    const d = dragRef.current; if (!d) return;
    if (d.kind === "move") {
      const dx = e.clientX - d.x0, dy = e.clientY - d.y0;
      if (!d.moved && Math.hypot(dx, dy) < 6) return;
      set({ ...d, moved: true, dx, dy, lane: laneAt(toBoard(e).y), off: !onScreen(e) });
    } else if (d.kind === "connect") {
      const pt = toBoard(e), seen = onScreen(e), tgt = seen ? nodeAt(pt) : null;
      const moved = d.moved || Math.hypot(e.clientX - d.x0, e.clientY - d.y0) >= 6;
      set({ ...d, moved, x: pt.x, y: pt.y, target: tgt && tgt !== d.from ? tgt : null, self: tgt === d.from, inBoard: seen && pt.x >= 0 && pt.x <= W && pt.y >= 0 && pt.y <= H });
    } else if (d.kind === "bg" && d.pan) {
      const dx = e.clientX - d.x0, dy = e.clientY - d.y0, el = p.scrollRef.current;
      if (!d.moved && Math.hypot(dx, dy) < 4) return;
      if (el) { el.scrollLeft = d.sx - dx; el.scrollTop = d.sy - dy; }
      if (!d.moved) set({ ...d, moved: true });
    }
  };
  const finish = (e: RPointerEvent<SVGSVGElement>) => {
    const d = dragRef.current; if (!d) return;
    set(null);
    const cancel = e.type === "pointercancel";
    if (cancel) return;
    if (d.kind === "move" && d.moved && !d.off) {
      const lane = doc.lanes[d.lane], s = B[d.id];
      if (lane && s && lane.id !== s.lane) p.onMoveLane(d.id, lane.id);
    } else if (d.kind === "connect") {
      if (d.target) p.onConnect(d.from, d.target);
      else if (!d.moved) p.onAddNext(d.from); // a click on the + handle
      else if (!d.self && d.inBoard) p.onAddNext(d.from, doc.lanes[laneAt(d.y)]?.id);
    } else if (d.kind === "bg" && !d.moved && Math.hypot(e.clientX - d.x0, e.clientY - d.y0) < 6) {
      if (d.edge) p.onSelect(d.edge); else if (p.sel) p.onSelect(null);
    }
  };

  const dropLane = drag?.kind === "move" && drag.moved && !drag.off ? drag.lane : drag?.kind === "connect" && drag.moved && !drag.target && !drag.self && drag.inBoard ? laneAt(drag.y) : null;
  const connectHint = (d: Extract<Drag, { kind: "connect" }>) => {
    if (d.target) return B[d.from]?.type === "decision" ? `Add a branch to ${B[d.target]?.name}` : `Connect to ${B[d.target]?.name}`;
    if (!d.moved || d.self) return null;
    if (!d.inBoard) return "Drop on a step, or in a lane";
    const lane = doc.lanes[laneAt(d.y)];
    return `Add a step in ${lane?.name ?? "this lane"}${lane?.agent ? " (agent)" : ""}`;
  };
  const hint = drag?.kind === "move" && drag.moved && drag.off ? "Drop it in a lane" : drag?.kind === "move" && drag.moved ? `Move to ${doc.lanes[drag.lane]?.name ?? ""}${doc.lanes[drag.lane]?.agent ? ": an agent does it" : ""}`
    : drag?.kind === "connect" ? connectHint(drag) : null;
  const hintAt = drag?.kind === "connect" ? { left: drag.x * z + 14, top: drag.y * z - 34 }
    : drag?.kind === "move" && L.pos[drag.id] ? { left: L.pos[drag.id].x * z + drag.dx + 14, top: L.pos[drag.id].y * z + drag.dy - 40 } : undefined;

  // The rail gets smaller words as the map zooms out, so lane names never run into each other.
  const railSize = LANEH * z < 52 ? "tiny" : LANEH * z < 84 ? "compact" : "";
  const selStep = p.sel ? B[p.sel] : null, selPos = p.sel ? L.pos[p.sel] : null;
  const showBar = !!(selStep && selPos && !p.ro && !p.rename && !(drag && (drag.kind !== "bg")));

  return (
    <div className="pmap-inner">
      <div className={`pmap-rail ${railSize}`} style={{ width: p.railWidth }}>
        {doc.lanes.map((l, i) => {
          const n = doc.steps.filter((s) => s.lane === l.id).length;
          const editing = p.laneRename?.id === l.id;
          return (
            <div key={l.id} className={`pmap-lane-cell${l.agent ? " agent" : ""}${dropLane === i ? " drop" : ""}`} style={{ height: LANEH * z }}
              onDoubleClick={() => { if (!p.ro && !editing) p.onLaneRename({ id: l.id, value: l.name }); }} title={p.ro ? undefined : "Double-click to rename the lane"}>
              {l.agent && <span className="pmap-agent-tag">Agent</span>}
              {editing ? (
                <input className="pmap-lane-input" autoFocus value={p.laneRename!.value} maxLength={60} aria-label="Lane name"
                  onFocus={(e) => { ended.current = false; e.currentTarget.select(); }} onChange={(e) => p.onLaneRename({ id: l.id, value: e.target.value })}
                  onBlur={() => { if (!ended.current) p.onLaneRename(p.laneRename, true); ended.current = false; }}
                  onKeyDown={(e) => { e.stopPropagation(); if (e.key === "Enter") { e.preventDefault(); ended.current = true; p.onLaneRename(p.laneRename, true, true); } else if (e.key === "Escape") { e.preventDefault(); ended.current = true; p.onLaneRename(null, false, true); } }} />
              ) : <span className="ln">{l.name}</span>}
              <span className="lc">{n} step{n === 1 ? "" : "s"}</span>
            </div>
          );
        })}
        {!p.ro && <button className="pmap-lane-add" onClick={p.onAddLane} title="Add a lane: a role, a department or an AI agent">+ Add lane</button>}
      </div>
      <div className="pmap-board" style={{ width: W * z, height: H * z }}>
        <svg ref={svgRef} className={`pmap-map${drag?.kind === "bg" && drag.moved ? " panning" : ""}`} viewBox={`0 0 ${W} ${H}`} width={W * z} height={H * z} role="img" aria-label={`Swimlane map with ${doc.steps.length} steps in ${doc.lanes.length} lanes`}
          onPointerDown={down} onPointerMove={move} onPointerUp={finish} onPointerCancel={finish}>
          <defs>
            <marker id="pmap-ah" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="8" markerHeight="8" markerUnits="userSpaceOnUse" orient="auto-start-reverse"><path className="pmap-ahp" d="M0 0L10 5L0 10z" /></marker>
            <marker id="pmap-ah-acc" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="9" markerHeight="9" markerUnits="userSpaceOnUse" orient="auto-start-reverse"><path className="pmap-ahp-acc" d="M0 0L10 5L0 10z" /></marker>
          </defs>
          {doc.lanes.map((l, i) => (
            <g key={l.id}>
              <rect className={`pmap-lane ${i % 2 ? "b" : "a"}${l.agent ? " agent" : ""}${dropLane === i ? " drop" : ""}`} x={0} y={i * LANEH} width={W} height={LANEH} />
              {i > 0 && <line className="pmap-lane-sep" x1={0} x2={W} y1={i * LANEH} y2={i * LANEH} />}
            </g>
          ))}
          {(() => {
            // Labels that would overlap (two branches into one step): the same word once, a different one a line up.
            const drawn: { x1: number; x2: number; y: number; text: string }[] = [];
            return doc.flows.map((f) => {
              const a = B[f.from], b = B[f.to];
              if (!a || !b || !L.pos[f.from] || !L.pos[f.to]) return null;
              const r = route(L, a, b), on = !!p.active?.edges.has(`${f.from}>${f.to}`), picked = p.sel === f.from;
              let ly = r.ly, show = !!f.label;
              const w = f.label.length * 6 + 4, x1 = r.anchor === "end" ? r.lx - w : r.anchor === "middle" ? r.lx - w / 2 : r.lx, x2 = x1 + w;
              for (let i = 0; show && i < 4; i++) {
                const hit = drawn.find((d) => d.x1 < x2 + 6 && x1 < d.x2 + 6 && Math.abs(d.y - ly) < 11);
                if (!hit) { drawn.push({ x1, x2, y: ly, text: f.label }); break; }
                if (hit.text === f.label) { show = on || picked; break; }
                ly -= 12;
              }
              return (
                <g key={`${f.from}>${f.to}`} className={`pmap-edge${on ? " path" : ""}${picked ? " out" : ""}${p.active && !on ? " dim" : ""}`} data-edge={`${f.from}>${f.to}`}>
                  <path className="hit" d={r.d} />
                  <path className="ln" d={r.d} markerEnd={`url(#${on ? "pmap-ah-acc" : "pmap-ah"})`} />
                  {show && <text x={r.lx} y={ly} textAnchor={r.anchor}>{f.label}</text>}
                </g>
              );
            });
          })()}
          {doc.steps.map((s) => L.pos[s.id] && (
            <Node key={s.id} s={s} x={L.pos[s.id].x} y={L.pos[s.id].y} diff={p.diff} comments={p.comments[s.id] ?? 0}
              sel={p.sel === s.id} on={!!p.active?.nodes.has(s.id)} dim={!!p.active && !p.active.nodes.has(s.id)} cur={p.walkCur === s.id} ro={p.ro}
              offset={drag?.kind === "move" && drag.moved && drag.id === s.id ? { dx: drag.dx / z, dy: drag.dy / z } : null} />
          ))}
          {drag?.kind === "connect" && drag.moved && L.pos[drag.from] && (
            <path className="pmap-temp" d={`M${L.pos[drag.from].x + sz(B[drag.from]).w / 2} ${L.pos[drag.from].y}L${drag.x} ${drag.y}`} />
          )}
        </svg>
        {p.rename && L.pos[p.rename.id] && B[p.rename.id] && (() => {
          const q = L.pos[p.rename.id], w = Math.max(sz(B[p.rename.id]).w, 150);
          return (
            <input className="pmap-rename" autoFocus value={p.rename.value} maxLength={120} aria-label="Step name"
              style={{ left: (q.x - w / 2) * z, top: (q.y - 16) * z, width: w * z, height: 32 * z, fontSize: Math.max(12, 13 * z) }}
              onFocus={(e) => { ended.current = false; if (p.rename?.all) e.currentTarget.select(); else e.currentTarget.setSelectionRange(e.currentTarget.value.length, e.currentTarget.value.length); }}
              onChange={(e) => p.onRenameChange(e.target.value)} onBlur={() => { if (!ended.current) p.onRenameEnd(true); ended.current = false; }}
              onKeyDown={(e) => { e.stopPropagation(); if (e.key === "Enter") { e.preventDefault(); ended.current = true; p.onRenameEnd(true, true); } else if (e.key === "Escape") { e.preventDefault(); ended.current = true; p.onRenameEnd(false, true); } }} />
          );
        })()}
        {showBar && <StepBar s={selStep!} x={selPos!.x * z} top={(selPos!.y - sz(selStep!).h / 2) * z} bottom={(selPos!.y + sz(selStep!).h / 2) * z} p={p} />}
        {hint && <div className="pmap-drag-hint" role="status" style={hintAt}>{hint}</div>}
      </div>
    </div>
  );
}

/** The selected step's toolbar: the common changes, one click each, with their keyboard shortcuts. It sits above the step
 *  (below when there's no room) and slides sideways to stay in the visible part of the map. */
function StepBar({ s, x, top, bottom, p }: { s: Step; x: number; top: number; bottom: number; p: Props }) {
  const ref = useRef<HTMLDivElement>(null);
  const [fit, setFit] = useState({ dx: 0, below: false, off: false });
  useLayoutEffect(() => {
    const sc = p.scrollRef.current; if (!sc) return;
    const place = () => {
      const el = ref.current, board = el?.parentElement; if (!el || !board) return;
      const v = sc.getBoundingClientRect(), b = board.getBoundingClientRect(), w = el.offsetWidth, h = el.offsetHeight;
      const lo = v.left + p.railWidth + 6, hi = v.left + sc.clientWidth - 6, left = b.left + x - w / 2;
      const dx = Math.round(left < lo ? lo - left : left + w > hi ? Math.max(lo - left, hi - (left + w)) : 0);
      const below = b.top + top - 8 - h < v.top + 4;
      // The step scrolled out of view: so does its toolbar, rather than acting on a step you can't see.
      const off = b.left + x < lo - 6 || b.left + x > hi + 6 || b.top + bottom < v.top || b.top + top > v.top + sc.clientHeight;
      setFit((f) => (f.dx === dx && f.below === below && f.off === off ? f : { dx, below, off }));
    };
    place();
    sc.addEventListener("scroll", place, { passive: true }); window.addEventListener("resize", place);
    return () => { sc.removeEventListener("scroll", place); window.removeEventListener("resize", place); };
  });
  const keep = () => p.scrollRef.current?.focus({ preventScroll: true });
  const act = (fn: () => void) => (e: React.MouseEvent) => { e.stopPropagation(); fn(); keep(); };
  const btn = (label: ReactNode, title: string, fn: () => void, cls = "") => <button type="button" className={cls} title={title} onPointerDown={(e) => e.stopPropagation()} onClick={act(fn)}>{label}</button>;
  return (
    <div ref={ref} className={`pmap-stepbar ${fit.below ? "below" : "above"}`} role="toolbar" aria-label={`${s.name}: changes`} style={{ left: x + fit.dx, top: fit.below ? bottom + 8 : top - 8, visibility: fit.off ? "hidden" : undefined }}>
      {s.type === "decision" ? btn("+ Branch", "Add a branch (Enter)", () => p.onAddNext(s.id))
        : s.type !== "end" && <>{btn("+ Step", "Add the next step (Enter)", () => p.onAddNext(s.id))}{btn("+ Decision", "Add a decision after this step (Tab)", () => p.onAddDecision(s.id))}</>}
      {btn("Rename", "Rename (or just start typing)", () => p.onStartRename(s.id))}
      {p.panelHidden && btn("Details", "Show the step's details", p.onShowPanel)}
      {s.type !== "start" && !(s.type === "end" && p.doc.steps.filter((x) => x.type === "end").length < 2) && btn("Delete", "Delete the step (Delete)", () => p.onDelete(s.id), "danger")}
    </div>
  );
}

function Node({ s, x, y, diff, comments, sel, on, dim, cur, ro, offset }: {
  s: Step; x: number; y: number; diff: Diff | null; comments: number; sel: boolean; on: boolean; dim: boolean; cur: boolean; ro: boolean; offset: { dx: number; dy: number } | null;
}) {
  const { w, h } = sz(s);
  const cls = ["pmap-node", s.type, sel && "sel", on && "path", dim && "dim", cur && "cur", offset && "dragging"].filter(Boolean).join(" ");
  const k = diff ? (diff.added.includes(s.id) ? "added" : diff.changed[s.id] ? "changed" : null) : null;
  const label = k === "added" ? "NEW" : "CHANGED";
  let body;
  if (s.type === "decision") {
    const ls = wrap(s.name, 15, 2);
    body = <><polygon className="shape" points={`${x - w / 2},${y} ${x},${y - h / 2} ${x + w / 2},${y} ${x},${y + h / 2}`} />{ls.map((t, i) => <text key={i} className="nm" x={x} y={y + 4 + (i - (ls.length - 1) / 2) * 14} textAnchor="middle">{t}</text>)}</>;
  } else if (s.type === "start" || s.type === "end") {
    const ls = wrap(s.name, 17, 2);
    body = <><rect className="shape" x={x - w / 2} y={y - h / 2} width={w} height={h} rx={h / 2} />{ls.map((t, i) => <text key={i} className="nm" x={x} y={y + 4 + (i - (ls.length - 1) / 2) * 14} textAnchor="middle">{t}</text>)}</>;
  } else {
    const ls = wrap(s.name, 19, 2), base = ls.length > 1 ? y - 12 : y - 4;
    let tag = sysText(s); if (tag.length > 21) tag = tag.slice(0, 20) + "…";
    const tw = tag.length * 6.3 + 11, tx = x - tw / 2;
    body = <>
      <rect className="shape" x={x - w / 2} y={y - h / 2} width={w} height={h} rx={4} />
      {ls.map((t, i) => <text key={i} className="nm" x={x} y={base + i * 15} textAnchor="middle">{t}</text>)}
      <rect className={`pmap-sq-${s.system}`} x={tx} y={y + 15} width={6} height={6} rx={1} />
      <text className="systag" x={tx + 11} y={y + 21}>{tag}</text>
    </>;
  }
  const tw = label.length * 6.6 + 10, tx = s.type === "task" ? x - w / 2 : x - tw / 2, ty = y - h / 2 - 17;
  const cx = x + w / 2 - (s.type === "decision" ? w / 4 : 2), cy = y - h / 2 + (s.type === "decision" ? h / 4 : 2);
  const hx = x + w / 2;
  return (
    <g className={cls} data-node={s.id} transform={offset ? `translate(${offset.dx} ${offset.dy})` : undefined}>
      <title>{s.name}</title>
      {body}
      {k && <g className={`pmap-dtag ${k}`}><rect x={tx} y={ty} width={tw} height={14} rx={2} /><text x={tx + 5} y={ty + 10}>{label}</text></g>}
      {comments > 0 && <g className="pmap-cbadge"><circle cx={cx} cy={cy} r={9} /><text x={cx} y={cy + 3.5} textAnchor="middle">{comments}</text></g>}
      {sel && !ro && s.type !== "end" && (
        <g className="pmap-handle" data-handle={s.id}>
          <title>{s.type === "decision" ? "Click to add a branch, or drag onto a step" : "Click to add the next step, drag onto a step to connect, or drag into a lane"}</title>
          <circle className="hit" cx={hx} cy={y} r={18} /><circle className="knob" cx={hx} cy={y} r={8} />
          <path className="plus" d={`M${hx - 3.5} ${y}H${hx + 3.5}M${hx} ${y - 3.5}V${y + 3.5}`} />
        </g>
      )}
    </g>
  );
}
